// The RBP Shopping List's add to cart, as SL1.0 runs it (`rbp_npr.js:206-248`): ask the server for
// the confirmation first; with nothing to add, say "No products to add!" and open nothing; else show
// the per-depot counts and, on confirm, add. A successful add records this sign-in's last RBP add to
// cart (the "Last Edited" fallback) and tells the page, which reads the list again. A cart held by a
// send is tried once more after the basket's wait, as every cart write is, then shown as a refusal.
// Nothing here changes the active trading account or navigates: the add never switches depot.
// One add at a time: a second confirm (a double click) and a new start while an add runs do
// nothing, so the add is never sent twice and its result is never dropped. The "nothing to add"
// and failure notices go when the list changes under them (`clearNotice`).
// Story 18.18 (ADO-4037): the add writes the carts on the server, outside the basket store, so a
// successful add then reads again what the app holds of them (`refreshCartsAfterAdd`): the open
// cart's lines, which the choice screen prefers over its own open-carts read, and the open-carts
// counts. Without it a cart the add created or filled stayed empty there until a page reload.

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  RbpRequestError,
  addRbpSelectionsToCart,
  fetchRbpCartAdditionPreview,
  type RbpCartAdditionPreview,
  type RbpCartAdditionResult,
} from '../api/rbp.client';
import { useSessionStore } from '../auth/useSessionStore';
import { useActiveBasketStore } from '../basket/useActiveBasketStore';
import { useBasketStore, waitForTheSendHold } from '../basket/useBasketStore';
import { reloadOrderingChoices } from '../ordering/orderingSession';
import { writeLastRbpAddToCartAt } from './rbpLastEdited';

/** One group's selections, or every selection (`benefitGroupCode` null). */
export interface RbpCartAdditionScope {
  readonly benefitGroupCode: string | null;
}

export type RbpCartAdditionState =
  | { readonly status: 'idle' }
  | { readonly status: 'previewing'; readonly scope: RbpCartAdditionScope }
  | {
      readonly status: 'confirm';
      readonly scope: RbpCartAdditionScope;
      readonly preview: RbpCartAdditionPreview;
    }
  | {
      readonly status: 'adding';
      readonly scope: RbpCartAdditionScope;
      readonly preview: RbpCartAdditionPreview;
    }
  | { readonly status: 'done'; readonly result: RbpCartAdditionResult }
  | { readonly status: 'nothing-to-add' }
  | { readonly status: 'failed'; readonly message: string };

const NOTHING_TO_ADD = 'rbp-nothing-to-add';
const CART_SENDING = 'cart-sending';
const FAILED_MESSAGE = 'We couldn’t add your products to the cart. Please try again.';

function isProblem(error: unknown, problemType: string): error is RbpRequestError {
  return error instanceof RbpRequestError && error.problemType === problemType;
}

function messageOf(error: unknown): string {
  // A typed refusal carries the server's own words; anything else is a generic failure.
  return error instanceof RbpRequestError && error.problemType !== null
    ? error.message
    : FAILED_MESSAGE;
}

/** Read again the carts the add may have changed: the basket the store holds (when one is open)
 *  and, once the choices have been read, the open carts, in the background (no loading state,
 *  and a failed read keeps the last good one). */
function refreshCartsAfterAdd(): void {
  const openCart = useActiveBasketStore.getState().cart;
  if (openCart !== null) void useBasketStore.getState().hydrate(openCart);
  if (useSessionStore.getState().orderingChoices !== null) {
    void reloadOrderingChoices({ background: true });
  }
}

export function useRbpCartAddition(onAdded: () => void): {
  readonly state: RbpCartAdditionState;
  readonly start: (scope: RbpCartAdditionScope) => void;
  readonly confirm: () => void;
  readonly dismiss: () => void;
  readonly clearNotice: () => void;
} {
  const [state, setState] = useState<RbpCartAdditionState>({ status: 'idle' });
  // A later start or a dismiss makes an answer in flight stale; an unmount drops it too.
  const latest = useRef(0);
  // Set synchronously on confirm, so two presses before a re-render send one add.
  const adding = useRef(false);
  const stateRef = useRef(state);
  const onAddedRef = useRef(onAdded);
  useEffect(() => {
    stateRef.current = state;
    onAddedRef.current = onAdded;
  });
  useEffect(
    () => () => {
      latest.current += 1;
    },
    [],
  );

  const start = useCallback((scope: RbpCartAdditionScope) => {
    if (adding.current) return;
    const request = ++latest.current;
    setState({ status: 'previewing', scope });
    void fetchRbpCartAdditionPreview(scope.benefitGroupCode)
      .then((preview) => {
        if (request !== latest.current) return;
        setState(
          preview.depots.length === 0
            ? { status: 'nothing-to-add' }
            : { status: 'confirm', scope, preview },
        );
      })
      .catch((error: unknown) => {
        if (request === latest.current) setState({ status: 'failed', message: messageOf(error) });
      });
  }, []);

  const confirm = useCallback(() => {
    const current = stateRef.current;
    if (current.status !== 'confirm' || adding.current) return;
    adding.current = true;
    const request = ++latest.current;
    const { scope, preview } = current;
    setState({ status: 'adding', scope, preview });
    const addOnceMoreIfHeld = async (): Promise<RbpCartAdditionResult> => {
      try {
        return await addRbpSelectionsToCart(scope.benefitGroupCode);
      } catch (error) {
        if (!isProblem(error, CART_SENDING)) throw error;
        await waitForTheSendHold();
        return addRbpSelectionsToCart(scope.benefitGroupCode);
      }
    };
    void addOnceMoreIfHeld()
      .finally(() => {
        adding.current = false;
      })
      .then((result) => {
        // The carts changed on the server even when this answer is no longer shown.
        refreshCartsAfterAdd();
        if (request !== latest.current) return;
        writeLastRbpAddToCartAt(result.addedAt);
        setState({ status: 'done', result });
        onAddedRef.current();
      })
      .catch((error: unknown) => {
        if (request !== latest.current) return;
        if (isProblem(error, NOTHING_TO_ADD)) {
          // The selections changed since the confirmation: say so and show the list as it is.
          setState({ status: 'nothing-to-add' });
          onAddedRef.current();
          return;
        }
        setState({ status: 'failed', message: messageOf(error) });
      });
  }, []);

  const dismiss = useCallback(() => {
    if (adding.current) return;
    latest.current += 1;
    setState({ status: 'idle' });
  }, []);

  const clearNotice = useCallback(() => {
    setState((current) =>
      current.status === 'nothing-to-add' || current.status === 'failed'
        ? { status: 'idle' }
        : current,
    );
  }, []);

  return { state, start, confirm, dismiss, clearNotice };
}
